import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["children"],
  _excluded2 = ["children"];
import React, { useContext, useState, useRef } from 'react';
import Box from 'components/Box';
import TextLink from 'components/TextLink';
import Icon from 'components/Icon';
import useUniqueId from 'hooks/useUniqueId';
import Flex from 'components/Flex';
function useDisclosure() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$initialState = _ref.initialState,
    initialState = _ref$initialState === void 0 ? false : _ref$initialState,
    controlledState = _ref.controlledState,
    setControlledState = _ref.setControlledState;
  var _useState = useState(initialState),
    _useState2 = _slicedToArray(_useState, 2),
    internalState = _useState2[0],
    setInternalState = _useState2[1];
  var isControlledState = controlledState !== undefined && setControlledState !== undefined;
  var isVisible = isControlledState ? controlledState : internalState;
  var setVisible = isControlledState ? setControlledState : setInternalState;
  var id = useUniqueId();
  var buttonRef = useRef(null);
  function focusButton() {
    var _buttonRef$current;
    (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
  }
  function show() {
    setVisible(true);
    focusButton();
  }
  function hide() {
    setVisible(false);
    focusButton();
  }
  function toggle() {
    setVisible(!isVisible);
    focusButton();
  }
  var buttonProps = {
    ref: buttonRef,
    onClick: toggle,
    'aria-controls': id,
    'aria-expanded': isVisible
  };
  var contentProps = {
    id: id,
    hidden: !isVisible,
    display: isVisible ? undefined : 'none'
  };
  return {
    isVisible: isVisible,
    show: show,
    hide: hide,
    toggle: toggle,
    buttonProps: buttonProps,
    contentProps: contentProps
  };
}
var DisclosureContext = /*#__PURE__*/React.createContext(null);
function Provider(_ref2) {
  var children = _ref2.children;
  var disclosure = useDisclosure();
  return /*#__PURE__*/React.createElement(DisclosureContext.Provider, {
    value: disclosure
  }, children);
}
function useDisclosureContext() {
  var context = useContext(DisclosureContext);
  if (!context) {
    throw new Error('useDisclosureContext must be used within a Disclosure.Provider');
  }
  return context;
}
function Toggle(_ref3) {
  var children = _ref3.children,
    otherProps = _objectWithoutProperties(_ref3, _excluded);
  var _useDisclosureContext = useDisclosureContext(),
    isVisible = _useDisclosureContext.isVisible,
    buttonProps = _useDisclosureContext.buttonProps;
  return /*#__PURE__*/React.createElement(TextLink, _extends({
    bold: true,
    stealthy: true,
    as: "button"
  }, otherProps, buttonProps), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "nav-right",
    vAlign: true,
    mr: "xxs",
    style: isVisible ? {
      transform: 'rotate(90deg)'
    } : undefined
  }), children));
}
function Hidden(_ref4) {
  var children = _ref4.children,
    otherProps = _objectWithoutProperties(_ref4, _excluded2);
  var _useDisclosureContext2 = useDisclosureContext(),
    contentProps = _useDisclosureContext2.contentProps;
  return /*#__PURE__*/React.createElement(Box, _extends({}, otherProps, contentProps), children);
}
var Disclosure = {
  Toggle: Toggle,
  Hidden: Hidden,
  Provider: Provider
};
export { useDisclosure };
export default Disclosure;